import { useEffect, useMemo, useState } from "react";
import { format } from "date-fns";
import { Download, Loader2, RefreshCw, Search, Users, ChevronsUpDown } from "lucide-react";
import { getAllLeaveBalances, getAllLeaves, editLeave } from "@/api/leave";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Modal } from "@/components/ui/modal";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { useToast } from "@/components/ui/use-toast";
import {
  FilterCountBadge,
  PeriodFilterBar,
  filterTriggerClass,
  usePeriodFilter,
} from "@/components/PeriodFilter";
import { toLocalWeekDate } from "@/utils/exportTimesheets";
import { cn } from "@/lib/utils";

type LeaveView = "applied" | "balance";

// Parse ISO-like datetime to a local display string.
const parseToLocal = (iso?: string) => {
  if (!iso) return "";
  const hasTZ = /Z|[+-]\d{2}:\d{2}$/.test(iso);
  const d = new Date(hasTZ ? iso : `${iso}Z`);
  return isNaN(d.getTime()) ? iso : d.toLocaleString();
};

const AdminLeaveTab = () => {
  const [view, setView] = useState<LeaveView>("applied");
  const [balances, setBalances] = useState<any[]>([]);
  const [leaves, setLeaves] = useState<any[]>([]);
  const [editModal, setEditModal] = useState<{ open: boolean; leave: any | null }>({ open: false, leave: null });
  const [editForm, setEditForm] = useState({ leave_date: "", duration: 1, remarks: "" });
  const [isEditing, setIsEditing] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  // Start in the loading state so the empty-table message doesn't flash first.
  const [isLoading, setIsLoading] = useState(true);
  const [isBalancesLoading, setIsBalancesLoading] = useState(true);
  const [leavesUpdated, setLeavesUpdated] = useState<Date | null>(null);
  const [balancesUpdated, setBalancesUpdated] = useState<Date | null>(null);
  const { toast } = useToast();

  // Leave Applied filters
  const period = usePeriodFilter();
  const { isDayInPeriod } = period;
  const [selectedEmployees, setSelectedEmployees] = useState<string[]>([]);
  const [isEmployeeFilterOpen, setIsEmployeeFilterOpen] = useState(false);
  const [search, setSearch] = useState("");

  // Leave Balance filter
  const [balanceSearch, setBalanceSearch] = useState("");

  useEffect(() => {
    fetchBalances();
    fetchLeaves();
  }, []);

  const fetchBalances = async () => {
    setIsBalancesLoading(true);
    try {
      const data = await getAllLeaveBalances();
      setBalances(data || []);
      setBalancesUpdated(new Date());
    } catch (error) {
      console.error("Error fetching leave balances:", error);
      toast({
        variant: "destructive",
        title: "Could not load leave balances",
        description: "Please check your connection and try refreshing.",
      });
    } finally {
      setIsBalancesLoading(false);
    }
  };

  const fetchLeaves = async () => {
    setIsLoading(true);
    try {
      const data = await getAllLeaves();
      setLeaves(data || []);
      setLeavesUpdated(new Date());
    } catch (error) {
      console.error("Error fetching leaves:", error);
      toast({
        variant: "destructive",
        title: "Could not load leave applications",
        description: "Please check your connection and try refreshing.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const openEditModal = (leave: any) => {
    setEditForm({
      leave_date: leave.leave_date,
      duration: leave.duration,
      remarks: leave.remarks || "",
    });
    setEditModal({ open: true, leave });
    setMessage(null);
  };

  const handleEdit = async () => {
    if (!editModal.leave) return;
    setIsEditing(true);
    setMessage(null);
    try {
      await editLeave(editModal.leave.leave_id, editForm);
      setEditModal({ open: false, leave: null });
      toast({ title: "Success", description: "Leave updated successfully." });
      fetchLeaves();
      fetchBalances();
    } catch (err: any) {
      setMessage(err?.response?.data?.message || "Failed to update leave.");
    } finally {
      setIsEditing(false);
    }
  };

  const availableYears = useMemo(
    () => [...new Set(leaves.map((leave) => toLocalWeekDate(leave.leave_date).getFullYear()))],
    [leaves]
  );

  const periodScopedLeaves = useMemo(
    () => leaves.filter((leave) => isDayInPeriod(toLocalWeekDate(leave.leave_date))),
    [leaves, isDayInPeriod]
  );

  const availableEmployees = useMemo(() => {
    const employeeMap = new Map<string, string>();
    periodScopedLeaves.forEach((leave) => {
      if (!employeeMap.has(leave.email)) employeeMap.set(leave.email, leave.display_name);
    });
    return [...employeeMap.entries()]
      .map(([email, name]) => ({ email, name: name || email }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [periodScopedLeaves]);

  // Drop employee selections that fall outside the active period.
  useEffect(() => {
    const employeeSet = new Set(availableEmployees.map((employee) => employee.email));
    setSelectedEmployees((prev) => {
      const next = prev.filter((email) => employeeSet.has(email));
      return next.length === prev.length ? prev : next;
    });
  }, [availableEmployees]);

  const filteredLeaves = useMemo(() => {
    const q = search.trim().toLowerCase();
    return periodScopedLeaves
      .filter((leave) => {
        const matchesEmployee =
          selectedEmployees.length === 0 || selectedEmployees.includes(leave.email);
        const matchesSearch =
          !q ||
          (leave.display_name || "").toLowerCase().includes(q) ||
          (leave.email || "").toLowerCase().includes(q) ||
          (leave.remarks || "").toLowerCase().includes(q);
        return matchesEmployee && matchesSearch;
      })
      .sort(
        (a, b) =>
          toLocalWeekDate(b.leave_date).getTime() - toLocalWeekDate(a.leave_date).getTime()
      );
  }, [periodScopedLeaves, selectedEmployees, search]);

  const totalLeaveDays = filteredLeaves.reduce((sum, leave) => sum + (Number(leave.duration) || 0), 0);

  const filteredBalances = useMemo(() => {
    const q = balanceSearch.trim().toLowerCase();
    return balances
      .filter(
        (user) =>
          !q ||
          (user.display_name || "").toLowerCase().includes(q) ||
          (user.email || "").toLowerCase().includes(q)
      )
      .sort((a, b) => (a.display_name || "").localeCompare(b.display_name || ""));
  }, [balances, balanceSearch]);

  const hasActiveFilters = selectedEmployees.length > 0 || search.trim().length > 0;

  const toggleEmployee = (email: string) => {
    setSelectedEmployees((prev) =>
      prev.includes(email) ? prev.filter((item) => item !== email) : [...prev, email]
    );
  };

  const clearAllFilters = () => {
    setSelectedEmployees([]);
    setSearch("");
  };

  const exportLeavesCSV = () => {
    if (!filteredLeaves.length) return;
    const headers = ["Leave ID", "Name", "Email", "Date", "Duration", "Applied At", "Remarks"];
    const rows = filteredLeaves.map((l) => [
      l.leave_id,
      l.display_name,
      l.email,
      l.leave_date,
      l.duration,
      l.applied_at,
      (l.remarks || "").replace(/[\r\n]+/g, " "),
    ]);
    const csvContent = [headers, ...rows]
      .map((r) =>
        r
          .map((field) => {
            if (field === null || field === undefined) return "";
            return `"${String(field).replace(/"/g, '""')}"`;
          })
          .join(",")
      )
      .join("\n");

    const blob = new Blob(["﻿", csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `leave_applications_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const viewButtonClass = (active: boolean) =>
    cn(
      "rounded-lg px-4 py-1.5 text-sm font-medium transition-colors",
      active
        ? "bg-background text-foreground shadow-sm"
        : "text-muted-foreground hover:text-foreground"
    );

  return (
    <div className="space-y-6">
      <div className="inline-flex rounded-xl border border-border/70 bg-muted/40 p-1" role="tablist">
        <button
          role="tab"
          aria-selected={view === "applied"}
          className={viewButtonClass(view === "applied")}
          onClick={() => setView("applied")}
        >
          Leave Applied
        </button>
        <button
          role="tab"
          aria-selected={view === "balance"}
          className={viewButtonClass(view === "balance")}
          onClick={() => setView("balance")}
        >
          Leave Balance
        </button>
      </div>

      {view === "applied" && (
        <div>
          <PeriodFilterBar
            period={period}
            availableYears={availableYears}
            onRefresh={fetchLeaves}
            isRefreshing={isLoading}
            lastUpdated={leavesUpdated}
          />

          <div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
            <div className="min-w-0">
              <h2 className="text-2xl font-bold text-foreground">Applied Leave Details</h2>
              <p className="mt-1 text-base text-muted-foreground">
                Showing {period.periodLabel} · {filteredLeaves.length} leave
                {filteredLeaves.length === 1 ? "" : "s"} · {totalLeaveDays} day
                {totalLeaveDays === 1 ? "" : "s"}
              </p>
            </div>

            <div className="flex flex-wrap items-center justify-end gap-2 rounded-2xl border border-border/70 bg-muted/30 p-2 lg:max-w-[70%]">
              <Popover open={isEmployeeFilterOpen} onOpenChange={setIsEmployeeFilterOpen}>
                <PopoverTrigger asChild>
                  <Button variant="outline" className={filterTriggerClass}>
                    <Users className="h-4 w-4" />
                    <span>Employees</span>
                    <FilterCountBadge count={selectedEmployees.length} />
                    <ChevronsUpDown className="h-3.5 w-3.5 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-80 p-0" align="end">
                  <Command>
                    <CommandInput placeholder="Search employees..." />
                    <CommandList>
                      <CommandEmpty>No employees found.</CommandEmpty>
                      <CommandGroup>
                        {availableEmployees.map((employee) => (
                          <CommandItem
                            key={employee.email}
                            value={`${employee.name} ${employee.email}`}
                            onSelect={() => toggleEmployee(employee.email)}
                            className="gap-2"
                          >
                            <Checkbox
                              checked={selectedEmployees.includes(employee.email)}
                              className="pointer-events-none"
                              aria-hidden
                            />
                            <div className="min-w-0 flex-1">
                              <div className="truncate font-medium">{employee.name}</div>
                              <div className="truncate text-xs text-muted-foreground">
                                {employee.email}
                              </div>
                            </div>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                  <div className="flex items-center justify-between border-t p-2">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() =>
                        setSelectedEmployees(availableEmployees.map((employee) => employee.email))
                      }
                    >
                      Select all
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setSelectedEmployees([])}>
                      Clear
                    </Button>
                  </div>
                </PopoverContent>
              </Popover>

              <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Search remarks, name, email"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="h-10 w-60 rounded-xl bg-background pl-9 shadow-sm"
                />
              </div>

              {hasActiveFilters && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearAllFilters}
                  className="h-10 rounded-xl text-muted-foreground"
                >
                  Clear all
                </Button>
              )}

              <button
                onClick={exportLeavesCSV}
                disabled={filteredLeaves.length === 0}
                className="flex h-10 items-center rounded-xl bg-primary px-4 text-white hover:bg-primary/90 disabled:opacity-50"
              >
                <Download className="mr-2 h-4 w-4" />
                Export CSV
                {hasActiveFilters && (
                  <span className="ml-1 rounded bg-white/20 px-1 text-xs">
                    ({filteredLeaves.length})
                  </span>
                )}
              </button>
            </div>
          </div>

          <Card
            className={cn("p-6 transition-opacity", isLoading && leavesUpdated && "opacity-60")}
            aria-busy={isLoading}
          >
            {isLoading && !leavesUpdated ? (
              <div className="py-10 text-center text-muted-foreground">
                <Loader2 className="mx-auto mb-2 h-6 w-6 animate-spin text-primary" />
                Loading leave applications…
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Duration</TableHead>
                    <TableHead>Applied At</TableHead>
                    <TableHead>Remarks</TableHead>
                    <TableHead>Edit</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredLeaves.map((leave) => (
                    <TableRow key={leave.leave_id}>
                      <TableCell>{leave.display_name}</TableCell>
                      <TableCell>{leave.email}</TableCell>
                      <TableCell>{leave.leave_date}</TableCell>
                      <TableCell>{leave.duration}</TableCell>
                      <TableCell>{parseToLocal(leave.applied_at)}</TableCell>
                      <TableCell>{leave.remarks}</TableCell>
                      <TableCell>
                        <button
                          className="rounded bg-primary px-2 py-1 text-primary-foreground hover:bg-primary/90"
                          onClick={() => openEditModal(leave)}
                        >
                          Edit
                        </button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {filteredLeaves.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={7} className="py-8 text-center text-muted-foreground">
                        No leave applications found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            )}
          </Card>
        </div>
      )}

      {view === "balance" && (
        <div>
          <div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
            <div className="min-w-0">
              <h2 className="text-2xl font-bold text-foreground">Leave Balances</h2>
              <p className="mt-1 flex items-center gap-1.5 text-base text-muted-foreground" aria-live="polite">
                <span>
                  {filteredBalances.length} of {balances.length} user
                  {balances.length === 1 ? "" : "s"}
                </span>
                {isBalancesLoading ? (
                  <>
                    <span aria-hidden>·</span>
                    <Loader2 className="h-3.5 w-3.5 animate-spin text-primary" />
                    <span className="text-sm text-primary">Loading data…</span>
                  </>
                ) : (
                  balancesUpdated && (
                    <span className="text-sm">· Updated {format(balancesUpdated, "h:mm a")}</span>
                  )
                )}
              </p>
            </div>

            <div className="flex flex-wrap items-center justify-end gap-2 rounded-2xl border border-border/70 bg-muted/30 p-2">
              <Button
                variant="outline"
                size="icon"
                onClick={() => fetchBalances()}
                disabled={isBalancesLoading}
                className="h-10 w-10 rounded-xl border-border/80 bg-background shadow-sm hover:bg-muted/50"
                aria-label="Refresh balances"
                title="Refresh data"
              >
                <RefreshCw className={cn("h-4 w-4", isBalancesLoading && "animate-spin")} />
              </Button>
              <div className="mx-0.5 h-8 w-px bg-border" aria-hidden />
              <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Search name or email"
                  value={balanceSearch}
                  onChange={(e) => setBalanceSearch(e.target.value)}
                  className="h-10 w-60 rounded-xl bg-background pl-9 shadow-sm"
                />
              </div>
            </div>
          </div>

          <Card
            className={cn("p-6 transition-opacity", isBalancesLoading && balancesUpdated && "opacity-60")}
            aria-busy={isBalancesLoading}
          >
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Available Leave</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isBalancesLoading && !balancesUpdated && (
                  <TableRow>
                    <TableCell colSpan={3} className="py-10 text-center text-muted-foreground">
                      <Loader2 className="mx-auto mb-2 h-6 w-6 animate-spin text-primary" />
                      Loading leave balances…
                    </TableCell>
                  </TableRow>
                )}
                {filteredBalances.map((user) => (
                  <TableRow key={user.user_id}>
                    <TableCell>{user.display_name}</TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>{user.available_leave}</TableCell>
                  </TableRow>
                ))}
                {filteredBalances.length === 0 && !isBalancesLoading && (
                  <TableRow>
                    <TableCell colSpan={3} className="py-8 text-center text-muted-foreground">
                      No users found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Card>
        </div>
      )}

      {editModal.open && (
        <Modal
          title="Edit Leave"
          description="Update leave details below."
          onConfirm={handleEdit}
          onCancel={() => setEditModal({ open: false, leave: null })}
          isProcessing={isEditing}
          confirmButtonLabel="Update"
        >
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium">Leave Date</label>
              <input
                type="date"
                className="mt-1 block w-full border rounded px-3 py-2"
                value={editForm.leave_date}
                onChange={e => setEditForm(f => ({ ...f, leave_date: e.target.value }))}
              />
            </div>
            <div>
              <label className="block text-sm font-medium">Duration</label>
              <input
                type="number"
                min={0.5}
                step={0.5}
                className="mt-1 block w-full border rounded px-3 py-2"
                value={editForm.duration}
                onChange={e => setEditForm(f => ({ ...f, duration: parseFloat(e.target.value) }))}
              />
            </div>
            <div>
              <label className="block text-sm font-medium">Remarks</label>
              <input
                type="text"
                className="mt-1 block w-full border rounded px-3 py-2"
                value={editForm.remarks}
                onChange={e => setEditForm(f => ({ ...f, remarks: e.target.value }))}
              />
            </div>
            {message && <div className="text-destructive">{message}</div>}
          </div>
        </Modal>
      )}
    </div>
  );
};

export default AdminLeaveTab;
